'use client'

import {useEffect} from 'react'
import Link from 'next/link'
import {FormattedMessage} from 'react-intl'

type ErrorPageProps = {
  error: Error & {digest?: string}
  reset: () => void
}

export default function ErrorPage({error, reset}: ErrorPageProps) {
  useEffect(() => {
    // Surface the error to the browser console while we don't have a real
    // error-tracking service wired up. Once Sentry / Vercel Error Monitoring
    // is in place, hook the report here.
    console.error('Application error:', error)
  }, [error])

  return (
    <main id="main" className="flex-1">
      <section className="container-narrow py-site-2xl" aria-labelledby="error-heading">
        <h1 id="error-heading" className="u-head-lg text-foreground">
          <FormattedMessage id="errors.page.title" defaultMessage="Something went wrong" />
        </h1>
        <p className="mt-site-sm u-body-md text-muted-foreground" role="alert">
          <FormattedMessage
            id="errors.page.description"
            defaultMessage="An unexpected error occurred while loading this page. You can try again, or head back to the homepage."
          />
        </p>
        <div className="mt-site-md flex flex-col gap-site-xs sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={reset}
            className="inline-flex h-[45px] items-center justify-center rounded-full bg-foreground px-6 text-base font-bold text-background hover:bg-foreground/90"
          >
            <FormattedMessage id="errors.page.tryAgain" defaultMessage="Try again" />
          </button>
          <Link
            href="/"
            className="inline-flex h-[45px] items-center justify-center rounded-full border border-foreground px-6 text-base font-bold text-foreground hover:bg-muted/25"
          >
            <FormattedMessage id="errors.page.goHome" defaultMessage="Go home" />
          </Link>
        </div>
        {error.digest ? (
          <p className="mt-site-lg u-body-sm text-muted-foreground">
            <FormattedMessage id="errors.page.referenceCode" defaultMessage="Reference code:" />{' '}
            <code className="font-mono">{error.digest}</code>
          </p>
        ) : null}
      </section>
    </main>
  )
}
